import React, { useState, useEffect } from 'react'
import { Sidebar } from '../components/layout/Sidebar'
import { useAuth } from '../lib/auth'
import {
  generateModalSplit,
  pngBase64ToBlob,
  pngBase64ToDataUrl,
  saveProject,
  listProvinces,
  listStedelijkheid,
  listMunicipalities,
  listTripPurposes,
  type ModalSplitInputs,
  type ProvinceMetadata,
  type StedelijkheidMetadata,
  type FilterAxis,
  type CellsReportEntry,
} from '../lib/sb-api-client'

// Filter axis options + Dutch labels (matches sb-api enum).
const FILTER_AXIS_OPTIONS: Array<{
  id: FilterAxis
  label: string
  hint: string
}> = [
  {
    id: 'vertrek',
    label: 'Vertrek (Susan-standaard)',
    hint: 'Trips die UIT het gebied vertrekken (pc_dep)',
  },
  {
    id: 'aankomst',
    label: 'Aankomst',
    hint: 'Trips die NAAR het gebied komen (pc_arr)',
  },
  {
    id: 'vertrek_of_aankomst',
    label: 'Vertrek óf aankomst',
    hint: 'Elke trip die het gebied raakt — begin OF eind (pc_dep OR pc_arr)',
  },
  {
    id: 'intern',
    label: 'Intern',
    hint: 'Alleen interne trips — begin ÉN eind in het gebied (pc_dep AND pc_arr)',
  },
]

// Time-of-day preset buckets — single source of truth for the form +
// the OGC time_dep_min / time_dep_max inputs. HHMM integers.
const TIME_PRESETS = [
  { id: 'all',         label: 'Alle uren',          min: undefined, max: undefined },
  { id: 'morning_peak', label: 'Ochtendspits 07-09', min: 700,       max: 900 },
  { id: 'day',         label: 'Dag 09-16',          min: 900,       max: 1600 },
  { id: 'evening_peak', label: 'Avondspits 16-18',   label_short: 'Avondspits', min: 1600, max: 1800 },
  { id: 'evening',     label: 'Avond 18-23',        min: 1800,      max: 2300 },
] as const

interface VierkantjesResult {
  title: string
  imageDataUrl: string
  total_trips: number
  generatedAt: string
  // Set after the optional save-to-history call resolves; until then,
  // the data URL is the only handle on the PNG.
  savedProjectId?: string
  // Per-cell sample breakdown returned by the OGC process. Drives the
  // collapsible "Sample report" panel under the chart so a consultant can
  // decide whether the sample is large enough for a given client engagement.
  cellsReport?: CellsReportEntry[]
  byMode?: Record<string, number>
  byDistance?: Record<string, number>
  filterAxis?: FilterAxis
}

// ──────────────────────────────────────────────────────────────────────────
// Sample report — pivot table mirroring the marimekko grid
// ──────────────────────────────────────────────────────────────────────────

const MODE_ORDER = ['Lopen', 'Fietsen', 'OV', 'Auto']
const DISTANCE_ORDER = ['<2,5km', '2,5-10km', '10-25km', '>25km']

function reliabilityColor(n: number): string {
  // Mirror sb-api thresholds (CONFIDENCE_LOW_THRESHOLD=30, HIGH=100).
  if (n >= 100) return 'text-emerald-700 bg-emerald-50'
  if (n >= 30) return 'text-orange-700 bg-orange-50'
  return 'text-red-700 bg-red-50'
}

function SampleReportTable({
  cells,
  byMode,
  byDistance,
  total,
}: {
  cells: CellsReportEntry[]
  byMode?: Record<string, number>
  byDistance?: Record<string, number>
  total: number
}) {
  // Build a lookup table keyed by mode|distance for O(1) cell access.
  const grid = new Map<string, CellsReportEntry>()
  for (const c of cells) grid.set(`${c.mode}|${c.distance}`, c)

  return (
    <div className="overflow-x-auto -mx-1">
      <table className="min-w-full text-xs sm:text-sm">
        <thead>
          <tr className="text-gray-600 border-b border-gray-200">
            <th className="text-left px-2 py-1.5 font-medium">Mode \\ Afstand</th>
            {DISTANCE_ORDER.map((d) => (
              <th key={d} className="text-right px-2 py-1.5 font-medium">{d}</th>
            ))}
            <th className="text-right px-2 py-1.5 font-medium border-l border-gray-300">Σ</th>
          </tr>
        </thead>
        <tbody>
          {MODE_ORDER.map((mode) => (
            <tr key={mode} className="border-b border-gray-100">
              <td className="px-2 py-1.5 font-medium text-gray-700">{mode}</td>
              {DISTANCE_ORDER.map((d) => {
                const entry = grid.get(`${mode}|${d}`)
                if (!entry) {
                  return (
                    <td key={d} className="text-right px-2 py-1.5 text-gray-300">—</td>
                  )
                }
                return (
                  <td
                    key={d}
                    className={`text-right px-2 py-1.5 ${reliabilityColor(entry.n_trips)}`}
                    title={`${entry.pct_of_total}% van totaal`}
                  >
                    <span className="font-medium">{entry.n_trips.toLocaleString()}</span>
                  </td>
                )
              })}
              <td className="text-right px-2 py-1.5 font-semibold text-gray-700 border-l border-gray-300">
                {byMode?.[mode]?.toLocaleString() ?? '—'}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-gray-300 text-gray-700">
            <td className="px-2 py-1.5 font-semibold">Σ</td>
            {DISTANCE_ORDER.map((d) => (
              <td key={d} className="text-right px-2 py-1.5 font-semibold">
                {byDistance?.[d]?.toLocaleString() ?? '—'}
              </td>
            ))}
            <td className="text-right px-2 py-1.5 font-bold text-sb-benzol-green border-l border-gray-300">
              {total.toLocaleString()}
            </td>
          </tr>
        </tfoot>
      </table>
      <div className="mt-2 text-xs text-gray-500 flex flex-wrap gap-3">
        <span><span className="inline-block w-2 h-2 rounded-full bg-emerald-500 mr-1"></span>≥100 ritten</span>
        <span><span className="inline-block w-2 h-2 rounded-full bg-orange-500 mr-1"></span>30–99 ritten</span>
        <span><span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-1"></span>&lt;30 ritten</span>
      </div>
    </div>
  )
}

function downloadCellsReportCsv(result: VierkantjesResult) {
  if (!result.cellsReport) return
  const lines = [
    'mode,distance,n_trips,pct_of_total',
    ...result.cellsReport.map(
      (c) => `${c.mode},${c.distance},${c.n_trips},${c.pct_of_total}`,
    ),
  ]
  const csv = lines.join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${result.title.replace(/[^a-z0-9]+/gi, '-')}-sample-report.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export default function Vierkantjes() {
  const { user } = useAuth()

  // Form state
  const [title, setTitle] = useState('')
  const [inputMode, setInputMode] = useState<'postcode' | 'municipality' | 'province'>('postcode')
  // Postcodes as tags: each entry is an exact PC4 ('1012') or trailing-X
  // wildcard pattern ('12XX', '1XXX'). Mix freely — sb-api expands each
  // pattern to a pc_dep BETWEEN range and OR-chains them.
  const [postcodeTags, setPostcodeTags] = useState<string[]>([])
  const [postcodeInput, setPostcodeInput] = useState('')
  const [selectedMunicipalities, setSelectedMunicipalities] = useState<string[]>([])
  const [municipalitySearch, setMunicipalitySearch] = useState('')
  const [municipalities, setMunicipalities] = useState<string[]>([])
  const [selectedProvinces, setSelectedProvinces] = useState<string[]>([])
  const [selectedStedelijkheid, setSelectedStedelijkheid] = useState('')
  const [yearMin, setYearMin] = useState(2004)
  const [yearMax, setYearMax] = useState(2023)

  // Advanced filters (default-off — Susan-standard behavior preserved)
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [selectedMotives, setSelectedMotives] = useState<string[]>([])
  const [timePreset, setTimePreset] = useState<string>('all')
  // Filter axis: which side of the trip the geo filter applies to.
  // Default 'vertrek' = Susan methodology (departure-side, destinations
  // aggregated). Lives in Geavanceerde so the default flow stays simple
  // for casual users while consultants can override.
  const [filterAxis, setFilterAxis] = useState<FilterAxis>('vertrek')

  // Sample-report panel collapsed by default — the PNG already carries the
  // overall reliability dot + total-N. Consultants who need per-cell counts
  // expand the panel.
  const [showSampleReport, setShowSampleReport] = useState(false)

  // UI state
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<VierkantjesResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Metadata
  const [provinces, setProvinces] = useState<ProvinceMetadata[]>([])
  const [stedelijkheidOptions, setStedelijkheidOptions] = useState<StedelijkheidMetadata[]>([])
  const [tripPurposes, setTripPurposes] = useState<string[]>([])

  // Load metadata on mount — all served by sb-api at api.studiobereikbaar.nl
  useEffect(() => {
    const loadMetadata = async () => {
      try {
        const [muniList, provList, stedList, motiveList] = await Promise.all([
          listMunicipalities(),
          listProvinces(),
          listStedelijkheid(),
          listTripPurposes(),
        ])
        setMunicipalities(muniList)
        setProvinces(provList)
        setStedelijkheidOptions(stedList)
        setTripPurposes(motiveList)
      } catch (err) {
        console.error('Failed to load metadata:', err)
      }
    }
    loadMetadata()
  }, [])

  const filteredMunicipalities = municipalities.filter((municipality) =>
    municipality.toLowerCase().includes(municipalitySearch.toLowerCase())
  )

  // Postcode tag helpers — mirrors the parser on sb-api side
  // (1-4 chars, digits or X, X must be trailing only)
  const isValidPostcodePattern = (s: string): boolean => {
    const cleaned = s.toUpperCase().trim()
    if (!/^[0-9X]{1,4}$/.test(cleaned)) return false
    const firstX = cleaned.indexOf('X')
    if (firstX === -1) return cleaned.length >= 1
    // Pure-X entries shorter than 4 chars are ambiguous; require at least 1 digit
    if (firstX === 0 && cleaned.length < 4) return false
    return cleaned.slice(firstX).split('').every((c) => c === 'X')
  }

  const tagDescription = (tag: string): string => {
    const t = tag.toUpperCase()
    if (!t.includes('X')) return `PC4 ${t} (exact)`
    const xCount = (t.match(/X/g) || []).length
    const prefix = t.replace(/X+$/, '')
    if (xCount === 4) return 'all PC4 (1000–9999)'
    const factor = 10 ** xCount
    const lo = Math.max(1000, parseInt(prefix || '0', 10) * factor)
    const hi = lo + factor - 1
    return `${lo}–${hi}`
  }

  const addPostcodeTag = (raw: string) => {
    const cleaned = raw.toUpperCase().trim()
    if (!cleaned) return
    if (!isValidPostcodePattern(cleaned)) {
      setError(`'${raw}' is not a valid postcode or wildcard pattern. Use 1–4 chars: digits + trailing X only.`)
      return
    }
    if (postcodeTags.includes(cleaned)) return
    setPostcodeTags((prev) => [...prev, cleaned])
    setPostcodeInput('')
    setError(null)
  }

  const handlePostcodeKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ' || e.key === 'Tab') {
      if (postcodeInput.trim()) {
        e.preventDefault()
        addPostcodeTag(postcodeInput)
      }
    } else if (e.key === 'Backspace' && postcodeInput === '' && postcodeTags.length > 0) {
      // Quick-remove last tag
      setPostcodeTags((prev) => prev.slice(0, -1))
    }
  }

  const handlePostcodePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text')
    if (text.includes(',') || text.includes(' ') || text.includes('\n')) {
      e.preventDefault()
      const tokens = text.split(/[,\s\n]+/).filter(Boolean)
      tokens.forEach((tok) => addPostcodeTag(tok))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setResult(null)

    const finalTitle = title || `Vierkantjes ${new Date().toLocaleDateString()}`

    try {
      // 1. Build OGC inputs from the form state
      const inputs: ModalSplitInputs = {
        title: finalTitle,
        year_min: yearMin,
        year_max: yearMax,
      }
      if (inputMode === 'postcode') {
        if (postcodeTags.length > 0) {
          inputs.postcodes = postcodeTags
        }
      } else if (inputMode === 'municipality') {
        inputs.municipalities = selectedMunicipalities
        if (selectedStedelijkheid) inputs.stedelijkheid = selectedStedelijkheid
      } else if (inputMode === 'province') {
        inputs.provincies = selectedProvinces
        if (selectedStedelijkheid) inputs.stedelijkheid = selectedStedelijkheid
      }

      // Advanced filters — apply only if user opened the panel + selected something
      if (selectedMotives.length > 0) {
        inputs.motives = selectedMotives
      }
      if (timePreset !== 'all') {
        const preset = TIME_PRESETS.find((p) => p.id === timePreset)
        if (preset) {
          if (preset.min !== undefined) inputs.time_dep_min = preset.min
          if (preset.max !== undefined) inputs.time_dep_max = preset.max
        }
      }
      // Always send filter_axis so the PNG parameter strip can label it
      // explicitly — even the default 'vertrek' is named on the chart.
      inputs.filter_axis = filterAxis

      // 2. Generate via OGC modal-split-analysis (api.studiobereikbaar.nl)
      const ogcResult = await generateModalSplit(inputs)
      const dataUrl = pngBase64ToDataUrl(ogcResult.image)

      // 3. Render immediately from the data URL
      const localResult: VierkantjesResult = {
        title: finalTitle,
        imageDataUrl: dataUrl,
        total_trips: ogcResult.total_trips,
        generatedAt: new Date().toISOString(),
        cellsReport: ogcResult.cells_report,
        byMode: ogcResult.by_mode,
        byDistance: ogcResult.by_distance,
        filterAxis,
      }
      setResult(localResult)

      // 4. Persist in user history (best-effort; failure here doesn't break UX)
      const ownerId = user?.email
      if (ownerId) {
        try {
          const blob = pngBase64ToBlob(ogcResult.image)
          const saved = await saveProject({
            pngBlob: blob,
            title: finalTitle,
            ownerId,
            ownerEmail: user?.email ?? undefined,
            ownerName: user?.name ?? undefined,
            sourceType: 'odin_query',
            parameters: inputs as Record<string, unknown>,
            totalTrips: ogcResult.total_trips,
          })
          setResult({ ...localResult, savedProjectId: saved.id })
        } catch (saveErr) {
          // Non-fatal — surface to console but keep the rendered result.
          console.warn('Failed to save Vierkantjes to project history:', saveErr)
        }
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : typeof err === 'string'
            ? err
            : 'Failed to generate vierkantjes'
      setError(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex h-screen" style={{ backgroundColor: '#F9FAFB' }}>
      {/* Sidebar */}
      <Sidebar activeItem="tools" user={user} />

      {/* Main content */}
      <main id="main-content" className="flex-1 overflow-auto pt-16 md:pt-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-8">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-h1 md:text-h1-desktop font-bold text-gray-900 mb-2">
            Vierkantjes Generator
          </h1>
          <p className="text-body text-gray-600">
            Generate modal split visualizations from ODIN travel data
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow p-4 md:p-6 mb-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 text-base border border-sb-light-grey rounded-md focus:ring-2 focus:ring-sb-benzol-green focus:border-sb-benzol-green"
                placeholder="e.g., Amsterdam Modal Split 2004-2023"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Selection Method
              </label>
              <div className="flex flex-col sm:flex-row gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setInputMode('postcode')}
                  className={`flex-1 px-4 py-2.5 rounded-md font-medium transition-colors text-sm sm:text-base ${
                    inputMode === 'postcode'
                      ? 'bg-sb-benzol-green text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Postcode
                </button>
                <button
                  type="button"
                  onClick={() => setInputMode('municipality')}
                  className={`flex-1 px-4 py-2.5 rounded-md font-medium transition-colors text-sm sm:text-base ${
                    inputMode === 'municipality'
                      ? 'bg-sb-benzol-green text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Municipality
                </button>
                <button
                  type="button"
                  onClick={() => setInputMode('province')}
                  className={`flex-1 px-4 py-2.5 rounded-md font-medium transition-colors text-sm sm:text-base ${
                    inputMode === 'province'
                      ? 'bg-sb-benzol-green text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  Province
                </button>
              </div>
            </div>

            {inputMode === 'postcode' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Postcodes
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Type a 4-digit PC4 (<span className="font-mono">1012</span>) or a trailing-X wildcard
                  (<span className="font-mono">12XX</span> = 1200-1299, <span className="font-mono">1XXX</span> = 1000-1999).
                  Press <kbd className="px-1 bg-gray-100 rounded">Enter</kbd>, comma, or space to add.
                </p>
                <div
                  className="w-full min-h-[44px] flex flex-wrap items-center gap-2 px-2 py-1.5 border border-sb-light-grey rounded-md bg-white focus-within:ring-2 focus-within:ring-sb-benzol-green focus-within:border-sb-benzol-green"
                  onClick={() => {
                    const input = document.getElementById('postcode-tag-input')
                    if (input) input.focus()
                  }}
                >
                  {postcodeTags.map((tag, i) => (
                    <span
                      key={`${tag}-${i}`}
                      className="inline-flex items-center gap-1 bg-sb-benzol-green/10 text-sb-benzol-green border border-sb-benzol-green/30 rounded-md px-2 py-1 text-sm"
                      title={tagDescription(tag)}
                    >
                      <span className="font-mono font-semibold">{tag}</span>
                      <span className="text-xs opacity-70">{tagDescription(tag)}</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          setPostcodeTags((prev) => prev.filter((_, idx) => idx !== i))
                        }}
                        className="ml-1 text-sb-benzol-green hover:text-red-600 font-bold leading-none"
                        aria-label={`Remove ${tag}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <input
                    id="postcode-tag-input"
                    type="text"
                    value={postcodeInput}
                    onChange={(e) => setPostcodeInput(e.target.value.toUpperCase())}
                    onKeyDown={handlePostcodeKey}
                    onPaste={handlePostcodePaste}
                    onBlur={() => {
                      if (postcodeInput.trim()) addPostcodeTag(postcodeInput)
                    }}
                    maxLength={4}
                    placeholder={postcodeTags.length === 0 ? 'e.g. 1012, 12XX, 1XXX' : ''}
                    className="flex-1 min-w-[100px] outline-none border-none bg-transparent text-sm py-1"
                  />
                </div>
                {postcodeTags.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setPostcodeTags([])}
                    className="text-xs text-red-600 hover:text-red-700 mt-2"
                  >
                    Clear all ({postcodeTags.length})
                  </button>
                )}
              </div>
            )}

            {inputMode === 'municipality' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Search Municipalities
                  </label>
                  <input
                    type="text"
                    value={municipalitySearch}
                    onChange={(e) => setMunicipalitySearch(e.target.value)}
                    className="w-full px-3 py-2 border border-sb-light-grey rounded-md focus:ring-2 focus:ring-sb-benzol-green focus:border-sb-benzol-green"
                    placeholder="Type to filter..."
                  />
                </div>

                <div className="border border-sb-light-grey rounded-md p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      {selectedMunicipalities.length} municipalities selected
                    </span>
                    {selectedMunicipalities.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectedMunicipalities([])}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Clear all
                      </button>
                    )}
                  </div>

                  <div className="max-h-72 overflow-y-auto space-y-1">
                    {filteredMunicipalities.length > 0 ? (
                      filteredMunicipalities.map((municipality) => (
                        <label
                          key={municipality}
                          className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedMunicipalities.includes(municipality)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedMunicipalities((prev) =>
                                  prev.includes(municipality) ? prev : [...prev, municipality]
                                )
                              } else {
                                setSelectedMunicipalities((prev) => prev.filter((m) => m !== municipality))
                              }
                            }}
                            className="mr-2 h-4 w-4 text-sb-benzol-green focus:ring-sb-benzol-green border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-700">{municipality}</span>
                        </label>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">No municipalities found.</p>
                    )}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Urbanization Level (optional)
                  </label>
                  <select
                    value={selectedStedelijkheid}
                    onChange={(e) => setSelectedStedelijkheid(e.target.value)}
                    className="w-full px-3 py-2 border border-sb-light-grey rounded-md focus:ring-2 focus:ring-sb-benzol-green focus:border-sb-benzol-green"
                  >
                    <option value="">All urbanization levels...</option>
                    {stedelijkheidOptions.map((option) => (
                      <option key={option.code} value={option.code}>
                        {option.label} ({option.code})
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {inputMode === 'province' && (
              <>
                <div className="border border-sb-light-grey rounded-md p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">
                      Select Provinces ({selectedProvinces.length}/12)
                    </span>
                    {selectedProvinces.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectedProvinces([])}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        Clear all
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {provinces.map((province) => (
                      <label
                        key={province.code}
                        className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedProvinces.includes(province.code)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedProvinces((prev) =>
                                prev.includes(province.code) ? prev : [...prev, province.code]
                              )
                            } else {
                              setSelectedProvinces((prev) => prev.filter((code) => code !== province.code))
                            }
                          }}
                          className="mr-2 h-4 w-4 text-sb-benzol-green focus:ring-sb-benzol-green border-gray-300 rounded"
                        />
                        <span className="text-sm text-gray-700">
                          {province.name} ({province.code})
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Urbanization Level (optional)
                  </label>
                  <select
                    value={selectedStedelijkheid}
                    onChange={(e) => setSelectedStedelijkheid(e.target.value)}
                    className="w-full px-3 py-2 border border-sb-light-grey rounded-md focus:ring-2 focus:ring-sb-benzol-green focus:border-sb-benzol-green"
                  >
                    <option value="">All urbanization levels...</option>
                    {stedelijkheidOptions.map((option) => (
                      <option key={option.code} value={option.code}>
                        {option.label} ({option.code})
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Year From
                </label>
                <input
                  type="number"
                  value={yearMin}
                  onChange={(e) => setYearMin(parseInt(e.target.value))}
                  min={2004}
                  max={2023}
                  className="w-full px-3 py-2 text-base border border-sb-light-grey rounded-md focus:ring-2 focus:ring-sb-benzol-green focus:border-sb-benzol-green"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Year To
                </label>
                <input
                  type="number"
                  value={yearMax}
                  onChange={(e) => setYearMax(parseInt(e.target.value))}
                  min={2004}
                  max={2023}
                  className="w-full px-3 py-2 text-base border border-sb-light-grey rounded-md focus:ring-2 focus:ring-sb-benzol-green focus:border-sb-benzol-green"
                />
              </div>
            </div>

            {/* Advanced filters — collapsible. Susan-default = empty/unchanged. */}
            <div className="border border-sb-light-grey rounded-md">
              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                className="w-full flex justify-between items-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md"
              >
                <span>Geavanceerde filters {(selectedMotives.length > 0 || timePreset !== 'all' || filterAxis !== 'vertrek') && <span className="ml-2 text-xs text-sb-benzol-green">●</span>}</span>
                <span className="text-gray-400">{showAdvanced ? '▾' : '▸'}</span>
              </button>
              {showAdvanced && (
                <div className="p-3 pt-0 space-y-3">
                  {/* Filter axis (vertrek / aankomst / vertrek_en_aankomst) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Filter zijde
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Welke kant van de trip moet aan de geografische filter voldoen.
                      Default = <strong>vertrek</strong> (Susan-standaard methodologie). De gekozen
                      zijde verschijnt onderaan de PNG bij Geography.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                      {FILTER_AXIS_OPTIONS.map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setFilterAxis(opt.id)}
                          className={`px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors text-left ${
                            filterAxis === opt.id
                              ? 'bg-sb-benzol-green text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          title={opt.hint}
                        >
                          <div className="font-medium">{opt.label}</div>
                          <div className={`text-xs mt-0.5 ${filterAxis === opt.id ? 'text-white/80' : 'text-gray-500'}`}>
                            {opt.hint}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Motive multi-select */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Motief (verplaatsingsdoel)
                    </label>
                    <p className="text-xs text-gray-500 mb-2">
                      Filter op één of meer trip-motieven. Leeg laten = alle motieven (Susan-standaard).
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-48 overflow-y-auto">
                      {tripPurposes.map((purpose) => (
                        <label
                          key={purpose}
                          className="flex items-center p-1.5 hover:bg-gray-50 rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedMotives.includes(purpose)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedMotives((prev) => [...prev, purpose])
                              } else {
                                setSelectedMotives((prev) => prev.filter((m) => m !== purpose))
                              }
                            }}
                            className="mr-2 h-4 w-4 text-sb-benzol-green focus:ring-sb-benzol-green border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-700">{purpose}</span>
                        </label>
                      ))}
                    </div>
                    {selectedMotives.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectedMotives([])}
                        className="text-xs text-red-600 hover:text-red-700 mt-1"
                      >
                        Clear ({selectedMotives.length} geselecteerd)
                      </button>
                    )}
                  </div>

                  {/* Time-of-day preset */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tijdvenster (vertrektijd)
                    </label>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                      {TIME_PRESETS.map((preset) => (
                        <button
                          key={preset.id}
                          type="button"
                          onClick={() => setTimePreset(preset.id)}
                          className={`px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors ${
                            timePreset === preset.id
                              ? 'bg-sb-benzol-green text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          {preset.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={
                loading ||
                (inputMode === 'postcode' && postcodeTags.length === 0) ||
                (inputMode === 'municipality' && selectedMunicipalities.length === 0) ||
                (inputMode === 'province' && selectedProvinces.length === 0)
              }
              className="w-full bg-sb-benzol-green text-white py-3 px-4 rounded-md font-medium text-base hover:bg-[#70C282] disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors touch-manipulation"
            >
              {loading ? 'Generating...' : 'Generate Vierkantjes'}
            </button>
          </form>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800 font-medium">Error</p>
            <p className="text-red-600 text-sm mt-1">{error}</p>
          </div>
        )}

        {/* Low trip warning */}
        {result && result.total_trips < 100 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <p className="text-yellow-800 font-medium">⚠️ Low Sample Size</p>
            <p className="text-yellow-700 text-sm mt-1">
              This analysis is based on only {result.total_trips.toLocaleString()} trips. Results may not be statistically significant.
              Consider broadening your filters.
            </p>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="bg-white rounded-lg shadow p-4 md:p-6">
            <h2 className="text-h2 md:text-h2-desktop font-semibold text-gray-900 mb-2">
              {result.title}
            </h2>

            {/* Trip count badge - prominent */}
            <div className="mb-4 inline-flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
              <span className="text-blue-700 font-medium">Total trips:</span>
              <span className="text-blue-900 font-bold text-lg">
                {result.total_trips?.toLocaleString() || 'N/A'}
              </span>
            </div>

            <div className="mb-4">
              <img
                src={result.imageDataUrl}
                alt={result.title}
                className="w-full h-auto rounded border border-gray-200"
              />
            </div>

            {/* Sample report — collapsible. Per-cell trip counts so a
                consultant can decide whether the sample is large enough
                for a given client engagement. */}
            {result.cellsReport && result.cellsReport.length > 0 && (
              <div className="border border-sb-light-grey rounded-md mb-4">
                <button
                  type="button"
                  onClick={() => setShowSampleReport((v) => !v)}
                  className="w-full flex justify-between items-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md"
                >
                  <span>Sample report (cellen × ritten)</span>
                  <span className="text-gray-400">{showSampleReport ? '▾' : '▸'}</span>
                </button>
                {showSampleReport && (
                  <div className="p-3 pt-0">
                    <p className="text-xs text-gray-500 mb-3">
                      Aantal ritten per (modaliteit × afstandsklasse) cel — zelfde basis als de
                      percentages op de PNG (raw COUNT, Susan-methode). Gebruik dit om te beoordelen
                      of de steekproef groot genoeg is voor je klantopdracht.
                    </p>

                    {/* Pivot table mirroring the marimekko grid */}
                    <SampleReportTable
                      cells={result.cellsReport}
                      byMode={result.byMode}
                      byDistance={result.byDistance}
                      total={result.total_trips}
                    />

                    <button
                      type="button"
                      onClick={() => downloadCellsReportCsv(result)}
                      className="mt-3 text-xs text-sb-benzol-green hover:underline"
                    >
                      Download als CSV
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={result.imageDataUrl}
                download={`${result.title}.png`}
                className="flex-1 bg-green-600 text-white py-3 px-4 rounded-md text-center font-medium text-base hover:bg-green-700 transition-colors touch-manipulation"
              >
                Download PNG
              </a>

              <button
                onClick={() => {
                  setResult(null)
                  setPostcodeTags([])
                  setPostcodeInput('')
                  setSelectedMunicipalities([])
                  setSelectedProvinces([])
                  setSelectedStedelijkheid('')
                  setMunicipalitySearch('')
                  setTitle('')
                }}
                className="flex-1 bg-gray-200 text-gray-700 py-3 px-4 rounded-md font-medium text-base hover:bg-gray-300 transition-colors touch-manipulation"
              >
                Generate Another
              </button>
            </div>

            <div className="mt-4 p-3 bg-gray-50 rounded text-sm text-gray-600">
              <p className="font-medium text-gray-900">Dataset Information</p>
              <p className="mt-1">Generated: {new Date(result.generatedAt).toLocaleString()}</p>
              {result.savedProjectId && (
                <p className="truncate">Saved to history: {result.savedProjectId}</p>
              )}
            </div>
          </div>
        )}

        {/* Help Text */}
        {!result && !loading && (
          <div className="bg-[#F3FBF5] border border-[#8BCC96] rounded-lg p-4">
            <p className="text-sb-black font-medium mb-2">💡 Quick Start</p>
            <ul className="text-sb-dark-grey text-sm space-y-1 list-disc list-inside">
              <li><strong>Postcode:</strong> add tags — exact PC4 (<span className="font-mono">1012</span>) or trailing-X wildcard (<span className="font-mono">12XX</span>, <span className="font-mono">1XXX</span>). Mix freely.</li>
              <li><strong>Municipality:</strong> select one or more municipalities (530+ available)</li>
              <li><strong>Province:</strong> select one or more provinces, optionally filter by urbanization</li>
              <li><strong>Geavanceerde filters</strong> (collapsible): motive (Werken / Onderwijs / …) and tijdvenster (ochtendspits / dag / avondspits)</li>
              <li>Select year range (2004–2023 available; COVID Mar 2020 – Mar 2022 always excluded)</li>
              <li>Result shows trips by distance × mode and self-explains via parameter strip</li>
            </ul>
          </div>
        )}
        </div>
      </main>
    </div>
  )
}
